import { API_BASE, DEMO_CLIENT_ID } from './config';
import { freshIdToken, renewNow } from './auth';
import type { ClientConfig, ConversationTurn, Lead, Session, VisitChoice } from './types';
import type { PhotoUpload } from './photos';

/** A refused request, with what the API said about it. The message reads as before ("409 {...}"). */
export class ApiError extends Error {
  status: number;
  body: { error?: string; lead?: Lead; exists?: boolean } | null;
  constructor(status: number, text: string) {
    super(`${status} ${text.slice(0, 200)}`);
    this.status = status;
    let body: ApiError['body'] = null;
    try {
      body = JSON.parse(text) as ApiError['body'];
    } catch {
      body = null;
    }
    this.body = body && typeof body === 'object' ? body : null;
  }
}

/** A signed-in request to the API: renewed and retried once if turned down. The admin CRM uses it too. */
export async function req<T>(session: Session, path: string, init?: RequestInit): Promise<T> {
  const send = (token: string) => fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...(init?.headers ?? {}),
    },
  });
  const token = await freshIdToken(session);
  let res = await send(token);
  // Turned down once (the sign-in lapsed early, or this computer's clock is off): renew, try once more.
  if (res.status === 401) res = await send(await renewNow(session, token));
  if (!res.ok) throw new ApiError(res.status, await res.text());
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

const SHOP_KEY = 'leadsirl.shop';
/** Support view: the shop LeadsIRL opened from the admin CRM, in this tab only. */
export function supportShop(): string | null {
  try {
    return sessionStorage.getItem(SHOP_KEY);
  } catch {
    return null;
  }
}
export function setSupportShop(id: string | null) {
  try {
    if (id) sessionStorage.setItem(SHOP_KEY, id); else sessionStorage.removeItem(SHOP_KEY);
  } catch { /* private window: the demo shop then */ }
}

/** Which shop a request is for: a contractor's own; for LeadsIRL, the shop in support view or the demo shop. */
function cid(session: Session) {
  return session.groups.includes('admin') ? (supportShop() || DEMO_CLIENT_ID) : session.clientId;
}

function scope(session: Session) {
  return `clientId=${encodeURIComponent(cid(session))}`;
}

/** GET /api/config. No fake fallback: a failed load must never be edited and saved over real settings. */
export async function getConfig(session: Session): Promise<ClientConfig> {
  const data = await req<{ config?: ClientConfig } & Partial<ClientConfig>>(session, `/api/config?${scope(session)}`);
  const config = (data.config ?? data) as ClientConfig;
  if (!config.clientId) throw new Error('Config not found');
  return config;
}

export async function saveConfig(session: Session, patch: Partial<ClientConfig>): Promise<void> {
  await req(session, `/api/config?${scope(session)}`, { method: 'PATCH', body: JSON.stringify(patch) });
}

const DEMO_LEAD: Lead = {
  clientId: DEMO_CLIENT_ID,
  leadId: '+15551230001',
  phone: '+15551230001',
  name: 'Demo Homeowner',
  source: 'missed-call',
  status: 'in-conversation',
  contactCount: 1,
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
};

const DEMO_TURNS: ConversationTurn[] = [
  { clientId: DEMO_CLIENT_ID, leadId: '+15551230001', timestamp: new Date().toISOString(), seq: 0, role: 'assistant', text: 'LeadsIRL: sorry we missed your call. What service are you looking for? Reply STOP to opt out.', channel: 'sms' },
  { clientId: DEMO_CLIENT_ID, leadId: '+15551230001', timestamp: new Date().toISOString(), seq: 1, role: 'lead', text: 'Need a roof estimate in Walled Lake', channel: 'sms' },
  { clientId: DEMO_CLIENT_ID, leadId: '+15551230001', timestamp: new Date().toISOString(), seq: 2, role: 'assistant', text: 'Got it. What zip is the house in?', channel: 'sms' },
];

/** Every lead, most recently active first. Follows the pages: the Contacts list needs them all. */
export async function listLeads(session: Session): Promise<Lead[]> {
  const id = cid(session);
  const all: Lead[] = [];
  let cursor = '';
  for (let page = 0; page < 10; page++) {
    const data = await req<{ leads?: Lead[]; cursor?: string } | Lead[]>(
      session,
      `/api/leads?clientId=${encodeURIComponent(id)}&limit=500${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ''}`,
    );
    if (Array.isArray(data)) return data;
    all.push(...(data.leads ?? []));
    if (!data.cursor) break;
    cursor = data.cursor;
  }
  return all;
}

/** Opened in the CRM: texts up to now count as read. */
export async function markSeen(session: Session, leadId: string): Promise<void> {
  // LeadsIRL in a shop's CRM (support view): their new texts stay new for them.
  if (session.groups.includes('admin') && supportShop()) return;
  await req(session, `/api/leads/${encodeURIComponent(leadId)}?${scope(session)}`, { method: 'PUT', body: JSON.stringify({ seen: true }) });
}

export type DoneChoice = 'booked' | 'talked' | 'no-answer' | 'not-interested' | '';

/** Handled a "Needs you" lead. No answer keeps it pinned as tried; the others clear it. */
export async function markDone(session: Session, leadId: string, outcome: DoneChoice): Promise<Lead> {
  const d = await req<{ lead: Lead }>(session, `/api/leads/${encodeURIComponent(leadId)}?${scope(session)}`, {
    method: 'PUT', body: JSON.stringify({ done: true, ...(outcome ? { outcome } : {}) }),
  });
  return d.lead;
}

/** An event on the shop's Google Calendar. `leadId` marks one we booked. */
export interface CalEvent {
  id: string;
  title: string;
  start: string;
  end: string;
  allDay: boolean;
  location?: string;
  link?: string;
  free?: boolean;
  leadId?: string;
  /** One day of a repeating event: a change here changes that day only. */
  repeats?: boolean;
  /** From someone else's invitation: only they can change it. */
  invited?: boolean;
  /** People invited to it (Google emails them about a change only when asked to). */
  guests?: number;
  /** Booked by the assistant, now or before: a change here never emails anyone. */
  booked?: boolean;
  /** Google's event color, '1' to '11' (none: the calendar's own color). */
  colorId?: string;
}

/** One of their own events, as the CRM sends it: instants, or days (YYYY-MM-DD, last one not included) when all day. */
export interface OwnEventInput { title?: string; start?: string; end?: string; allDay?: boolean; location?: string; free?: boolean; colorId?: string }

/** Add one of their own events to the calendar (not an estimate). */
export async function addOwnEvent(session: Session, ev: OwnEventInput): Promise<CalEvent> {
  const r = await req<{ event: CalEvent }>(session, `/api/calendar/events?${scope(session)}`, { method: 'POST', body: JSON.stringify(ev) });
  return r.event;
}

/**
 * Change one of their own events; only what is sent changes. One day of a repeating event: that
 * day only. `notify`: email its guests about it (never for one the assistant booked).
 */
export async function changeOwnEvent(session: Session, id: string, ev: OwnEventInput, notify = false): Promise<CalEvent> {
  const r = await req<{ event: CalEvent }>(session, `/api/calendar/events/${encodeURIComponent(id)}?${scope(session)}`, { method: 'PATCH', body: JSON.stringify({ ...ev, notify }) });
  return r.event;
}

/** Delete one of their own events (one day of a repeating one: that day only). `notify`: email its guests. */
export async function deleteOwnEvent(session: Session, id: string, notify = false): Promise<void> {
  await req(session, `/api/calendar/events/${encodeURIComponent(id)}?${scope(session)}${notify ? '&notify=1' : ''}`, { method: 'DELETE' });
}
export interface CalendarRead {
  connected: boolean;
  /** Which calendar: only Google's events are read here so far. */
  provider?: 'google' | 'outlook' | null;
  /** When the assistant may book, Sunday first. */
  windows: { start: string; end: string }[][];
  durationMinutes: number;
  timezone: string;
  events: CalEvent[];
  /** Leads that just followed the calendar (moved or deleted there). */
  changes: { leadId: string; change: 'moved' | 'deleted'; appointmentAt: string }[];
  error?: string;
}

/** Everything on the calendar between two instants, read fresh from Google. */
export async function getCalendarEvents(session: Session, start: Date, end: Date): Promise<CalendarRead> {
  return req<CalendarRead>(session,
    `/api/calendar/events?start=${encodeURIComponent(start.toISOString())}&end=${encodeURIComponent(end.toISOString())}&${scope(session)}`);
}

/** How the booked estimate or call went. `revenue` goes with a win. Returns the lead as saved. */
export async function markVisit(session: Session, leadId: string, visit: VisitChoice, revenue?: number): Promise<Lead> {
  const d = await req<{ lead: Lead }>(session, `/api/leads/${encodeURIComponent(leadId)}?${scope(session)}`, {
    method: 'PUT', body: JSON.stringify({ visit, ...(revenue != null ? { revenue } : {}) }),
  });
  return d.lead;
}

/** Status, job value, name... Returns the lead as saved. */
export async function updateLeadFields(session: Session, leadId: string, attrs: Partial<Lead>): Promise<Lead> {
  const d = await req<{ lead: Lead }>(session, `/api/leads/${encodeURIComponent(leadId)}?${scope(session)}`, {
    method: 'PUT', body: JSON.stringify(attrs),
  });
  return d.lead;
}

export async function getConversation(session: Session, leadId: string): Promise<ConversationTurn[]> {
  const id = cid(session);
  const data = await req<{ turns?: ConversationTurn[] } | ConversationTurn[]>(
    session,
    `/api/leads/${encodeURIComponent(leadId)}/conversation?clientId=${encodeURIComponent(id)}`,
  );
  return Array.isArray(data) ? data : data.turns ?? [];
}

export async function patchLead(session: Session, leadId: string, attrs: Partial<Lead>): Promise<void> {
  await req(session, `/api/leads/${encodeURIComponent(leadId)}?${scope(session)}`, { method: 'PUT', body: JSON.stringify(attrs) });
}

// ------------------------------------------------------------ admin Agent screen
export interface AgentPreview {
  defaults: Record<string, string>;
  sections: Record<string, string>;
  overridden: string[];
  complianceEnabled: boolean;
  handoffEnabled: boolean;
  handoffPausesAi?: boolean;
  openingLines: string[];
  openingLinesAfterHours: string[];
  openingLinesOvernight: string[];
  quietHours: { start: string; end: string };
  timeBand: 'open' | 'afterHours' | 'overnight';
  openingUsesDefaults: boolean;
  answerHoursMode: 'booking' | 'custom';
  answerHours: { start: string; end: string; days: number[] };
  withinAnswerHours: boolean;
  soon: string;
  /** "19:30", or null when it never asks. */
  lateCallCutoff?: string | null;
  /** How a promise to call would be put right now. */
  callTiming?: { kind: 'soon' } | { kind: 'ask'; later: string; next: string; question: string } | { kind: 'later'; next: string };
  openingPreview: string | null;
  coaching: string;
  maxReplyChars: number;
  temperature: number;
  modelId: string;
  defaultModelId: string;
  newLeadPrompt: string;
  returningLeadPrompt: string;
  openingEvent: string;
  tools: string[];
}
export interface ModelOption { id: string; name: string; provider: string; kind: 'direct' | 'us' | 'global' | 'other' }
export interface ModelTest { ok: boolean; modelId: string; latencyMs: number; reply?: string; error?: string }

/** Config plus (admins only) every instruction the AI gets and a live preview. */
export async function getAgent(session: Session): Promise<{ config: ClientConfig; agent?: AgentPreview }> {
  const data = await req<{ config: ClientConfig; agent?: AgentPreview }>(session, `/api/config?${scope(session)}`);
  if (!data.config?.clientId) throw new Error('Config not found');
  return data;
}

/** PATCH any settings the portal accepts. The server validates and enforces admin-only fields. */
export async function saveSettings(session: Session, patch: Record<string, unknown>): Promise<void> {
  await req(session, `/api/config?${scope(session)}`, { method: 'PATCH', body: JSON.stringify(patch) });
}

/** Live from AWS every call. */
export async function listModels(session: Session) {
  return req<{ models: ModelOption[]; defaultModelId: string; fetchedAt: string }>(session, `/api/agent/models?${scope(session)}`);
}

export async function testModel(session: Session, modelId: string) {
  return req<ModelTest>(session, `/api/agent/test-model?${scope(session)}`, { method: 'POST', body: JSON.stringify({ modelId }) });
}

// ------------------------------------------------------------ owner replies
/** Text a lead from the shop's number. The server pauses the AI on that lead. */
/** Text the lead. `photos` are new uploads; `fileKeys` are photos already on the lead ("Text it"). */
export async function sendMessage(session: Session, leadId: string, text: string, photos: PhotoUpload[] = [], fileKeys: string[] = []) {
  const files = photos.map(({ contentType, data }) => ({ contentType, data }));
  return req<{ ok: boolean; turn: ConversationTurn; aiPaused: boolean }>(
    session, `/api/leads/${encodeURIComponent(leadId)}/messages?${scope(session)}`,
    { method: 'POST', body: JSON.stringify({ text, ...(files.length ? { photos: files } : {}), ...(fileKeys.length ? { fileKeys } : {}) }) },
  );
}

/**
 * Tell the homeowner about a calendar change (moved or cancelled in Google). A heads-up: it does
 * not pause the assistant. Returns the lead as saved.
 */
export async function sendNotice(session: Session, leadId: string, text: string, changeAt?: string): Promise<Lead> {
  const d = await req<{ lead: Lead }>(
    session, `/api/leads/${encodeURIComponent(leadId)}/messages?${scope(session)}`,
    { method: 'POST', body: JSON.stringify({ text, notice: 'calendar', ...(changeAt ? { changeAt } : {}) }) },
  );
  return d.lead;
}

/**
 * Move a booked estimate, change its length, or both (no end: it keeps its length). The
 * calendar changes first. `notice`: the homeowner should hear, the text is ready. `note`: worth
 * knowing (it overlaps something, or is outside the booking hours).
 */
export async function moveAppointment(session: Session, leadId: string, start: string, end?: string) {
  return req<{ lead: Lead; notice: boolean; note: string }>(
    session, `/api/leads/${encodeURIComponent(leadId)}/appointment?${scope(session)}`,
    { method: 'PUT', body: JSON.stringify({ start, ...(end ? { end } : {}) }) },
  );
}

/**
 * Book an estimate (or a call, kind 'phone') for a lead, on the calendar the way the assistant
 * books one. No end: the usual length. `notice`: the text for them is ready. `note`: worth knowing.
 */
export async function bookAppointment(session: Session, leadId: string, start: string, end?: string, kind: 'in-person' | 'phone' = 'in-person') {
  return req<{ lead: Lead; notice: boolean; note: string }>(
    session, `/api/leads/${encodeURIComponent(leadId)}/appointment?${scope(session)}`,
    { method: 'POST', body: JSON.stringify({ start, ...(end ? { end } : {}), kind }) },
  );
}

/** Cancel a booked estimate: marked cancelled on the calendar, the time freed. Then let them know. */
export async function cancelAppointment(session: Session, leadId: string) {
  return req<{ lead: Lead; notice: boolean }>(
    session, `/api/leads/${encodeURIComponent(leadId)}/appointment?${scope(session)}`, { method: 'DELETE' },
  );
}

/** "I already told them" about a calendar change. Marks that change only; nothing else in Needs you. */
/** A contact you added by hand. 409 with `exists` and the lead when the number is already a contact. */
export interface NewContact { name: string; phone: string; job?: string; address?: string; textsOk: boolean }
export async function addContact(session: Session, c: NewContact): Promise<Lead> {
  const d = await req<{ lead: Lead }>(session, `/api/leads?${scope(session)}`, { method: 'POST', body: JSON.stringify(c) });
  return d.lead;
}

/** They said it's OK to text them (a contact you added). */
export async function markTextsOk(session: Session, leadId: string): Promise<Lead> {
  const d = await req<{ lead: Lead }>(session, `/api/leads/${encodeURIComponent(leadId)}?${scope(session)}`, {
    method: 'PUT', body: JSON.stringify({ textsOk: true }),
  });
  return d.lead;
}

export async function markCalendarTold(session: Session, leadId: string, changeAt: string): Promise<Lead> {
  const d = await req<{ lead: Lead }>(session, `/api/leads/${encodeURIComponent(leadId)}?${scope(session)}`, {
    method: 'PUT', body: JSON.stringify({ calendarTold: changeAt }),
  });
  return d.lead;
}

/** Add one photo to a lead, privately: a good copy to keep and a small one for texting later. */
export async function addLeadPhoto(session: Session, leadId: string, photo: PhotoUpload, textCopy: PhotoUpload) {
  const strip = ({ contentType, data }: PhotoUpload) => ({ contentType, data });
  return req<{ ok: boolean; lead: Lead }>(
    session, `/api/leads/${encodeURIComponent(leadId)}/files?${scope(session)}`,
    { method: 'POST', body: JSON.stringify({ photo: strip(photo), textCopy: strip(textCopy) }) },
  );
}

export async function removeLeadPhoto(session: Session, leadId: string, key: string) {
  return req<{ ok: boolean; lead: Lead }>(
    session, `/api/leads/${encodeURIComponent(leadId)}/files?key=${encodeURIComponent(key)}&${scope(session)}`,
    { method: 'DELETE' },
  );
}

export async function setAiPaused(session: Session, leadId: string, paused: boolean) {
  return req<{ ok: boolean; aiPaused: boolean }>(
    session, `/api/leads/${encodeURIComponent(leadId)}/ai?${scope(session)}`,
    { method: 'POST', body: JSON.stringify({ paused }) },
  );
}

// ------------------------------------------------------------ lead details
export interface LeadField {
  when?: 'before' | 'after' | 'passive'; key: string; label: string; question: string; ask: boolean; required: boolean }

/** The shop's Info to collect list (built-in defaults if never edited) and the auto-Qualified switch. */
export async function getLeadSettings(session: Session) {
  const d = await req<{ config: ClientConfig & { autoQualify?: boolean }; fields?: LeadField[] }>(session, `/api/config?${scope(session)}`);
  return { fields: d.fields ?? [], autoQualify: d.config?.autoQualify !== false };
}

export async function getLeadFields(session: Session): Promise<LeadField[]> {
  return (await getLeadSettings(session)).fields;
}

export async function getLeadById(session: Session, leadId: string): Promise<Lead> {
  const d = await req<{ lead: Lead }>(session, `/api/leads/${encodeURIComponent(leadId)}?${scope(session)}`);
  return d.lead;
}

/** Owner edits. Empty value clears the field so the AI can fill it again. */
export async function saveLeadDetails(session: Session, leadId: string, details: Record<string, string>) {
  return req<{ ok: boolean; lead: Lead }>(
    session, `/api/leads/${encodeURIComponent(leadId)}/details?${scope(session)}`,
    { method: 'PUT', body: JSON.stringify({ details }) },
  );
}

// ------------------------------------------------------------ calendar
export interface CalendarSettings {
  bookingEnabled: boolean;
  estimateColorId: string;
  callbackColorId: string;
  timezone: string;
  businessHours: { start: string; end: string; days: number[] };
  /** Per weekday, "0".."6". Empty list means closed. null means not set yet: fall back to businessHours. */
  bookingWindows: Record<string, { start: string; end: string }[]> | null;
  /** Jobs the assistant always books a visit for. Free text. */
  quoteJobs: string;
  /** Anything else gets a visit only if likely over this many dollars. */
  visitThresholdUsd: number;
  appointmentDurationMinutes: number;
  bookingBufferMinutes: number;
  bookingLeadHours: number;
  bookingDaysAhead: number;
  /** Moved or cancelled an estimate in Google: text the homeowner automatically (else ask the owner). */
  calendarAutoText?: boolean;
  calendarOwnerText?: boolean;
}
export interface CalendarInfo {
  connected: boolean;
  provider: string | null;
  connectedAt: string | null;
  calendarError: string;
  settings: CalendarSettings;
  openTimes: { startIso: string; label: string }[];
  upcoming: { leadId: string; name?: string; phone?: string; appointmentAt?: string; service?: string; address?: string; summary?: string }[];
}

export async function getCalendar(session: Session) {
  return req<CalendarInfo>(session, `/api/calendar?${scope(session)}`);
}

/** One estimate start in the preview, as the agent sees it. */
export interface PreviewStart {
  startIso: string;
  clock: string;
  status: 'open' | 'busy' | 'too-soon';
  /** One of the times offered first. */
  offered: boolean;
}
export interface PreviewDay { ymd: string; label: string; closed: boolean; starts: PreviewStart[] }

/** The next 7 days for the settings on screen, saved or not. Nothing is saved. */
export async function previewCalendar(session: Session, settings: Partial<CalendarSettings>) {
  const r = await req<{ days: PreviewDay[] }>(session, `/api/calendar/preview?${scope(session)}`, {
    method: 'POST', body: JSON.stringify({ settings }),
  });
  return r.days;
}

/** Returns the Google sign-in URL. The browser goes there, then comes back to the CRM. */
export async function startCalendarConnect(session: Session, provider: 'google' | 'outlook' = 'google') {
  const r = await req<{ authorizeUrl: string }>(session, `/api/oauth/start?${scope(session)}`, {
    method: 'POST', body: JSON.stringify({ provider }),
  });
  return r.authorizeUrl;
}

export async function disconnectCalendar(session: Session) {
  await req(session, `/api/calendar/disconnect?${scope(session)}`, { method: 'POST', body: '{}' });
}

// ------------------------------------------------------------ Emulate Call
export interface SimState {
  lead: Lead & { details?: Record<string, { value: string; by: string; at: string }>; summary?: string };
  turns: ConversationTurn[];
}

export async function simStart(session: Session, source: 'missed-call' | 'inbound-sms') {
  return req<{ leadId: string; source: string; startedAt: string }>(
    session, `/api/simulate/start?${scope(session)}`, { method: 'POST', body: JSON.stringify({ source }) });
}

export async function simSay(session: Session, leadId: string, text: string, photos: PhotoUpload[] = []) {
  const files = photos.map(({ contentType, data }) => ({ contentType, data }));
  return req<{ ok: boolean; sentAt: string }>(
    session, `/api/simulate/${encodeURIComponent(leadId)}/say?${scope(session)}`,
    { method: 'POST', body: JSON.stringify({ text, ...(files.length ? { photos: files } : {}) }) });
}

export async function simState(session: Session, leadId: string) {
  return req<SimState>(session, `/api/simulate/${encodeURIComponent(leadId)}?${scope(session)}`);
}

export async function simExport(session: Session, leadId: string) {
  return req<Record<string, unknown>>(session, `/api/simulate/${encodeURIComponent(leadId)}/export?${scope(session)}`);
}
